/**
 * Draft, history, navigation, and save-status transitions for the Auto Assign dialog.
 *
 * @remarks
 * The reducer has no React or network effects. It stores pending direct-journey
 * choices separately from the route; the owning hook prepares the final projection
 * and performs persistence. Assignment calculations live in `journeys.ts`.
 *
 * @packageDocumentation
 */
import {
    AssignmentPlan,
    assignmentKey,
    needsAssignmentReview,
} from '../../../../../common/flight-leg/assignment-plan';
import {confirmJourney} from './journeys';

/** Editable route and assignment choices, including deferred direct journeys. */
export interface AutoAssignDraft {
    plan: AssignmentPlan;
    roundTrip: boolean;
    extend: boolean;
    directJourneys?: string[];
}

/** Review session state with draft history and a separate final-review projection. */
export interface AutoAssignState {
    draft: AutoAssignDraft;
    past: AutoAssignDraft[];
    future: AutoAssignDraft[];
    step: 0 | 1 | 2;
    finalPlan?: AssignmentPlan;
    showSteps: boolean;
    reviewKeys: string[];
    selectionView: string;
    saving: boolean;
    saveFailed: boolean;
    error?: string;
}

/** Typed user actions and save lifecycle events handled by the review reducer. */
export type AutoAssignAction =
    | {type: 'edit-route'; draft: AutoAssignDraft}
    | {type: 'choose'; keys: string[]; choice: number | 'unassign' | 'extend'}
    | {
          type:
              | 'undo'
              | 'redo'
              | 'back'
              | 'edit'
              | 'review-choices'
              | 'save-started'
              | 'save-finished';
      }
    | {type: 'review-final'; plan: AssignmentPlan}
    | {type: 'view'; view: string}
    | {type: 'error'; message: string}
    | {type: 'save-failed'; message: string};

const pendingKeys = (plan: AssignmentPlan) =>
    plan.rows.filter((row) => row.status === 'review').map(assignmentKey);
const reviewKeys = (state: AutoAssignState, plan: AssignmentPlan) =>
    Array.from(new Set([...state.reviewKeys, ...pendingKeys(plan)]));

/** Initializes a review session, showing steps only when assignments need review. */
export function createAutoAssignState(initial: {
    plan: AssignmentPlan;
    roundTrip: boolean;
    extend?: boolean;
}): AutoAssignState {
    return {
        draft: {...initial, extend: initial.extend ?? true},
        past: [],
        future: [],
        step: 0,
        showSteps: needsAssignmentReview(initial.plan),
        reviewKeys: pendingKeys(initial.plan),
        selectionView: 'groups',
        saving: false,
        saveFailed: false,
    };
}

/** Records an undoable edit and invalidates any previously computed final review. */
function updateDraft(
    state: AutoAssignState,
    draft: AutoAssignDraft,
    routeChanged: boolean
): AutoAssignState {
    return {
        ...state,
        draft,
        past: [...state.past, state.draft],
        future: [],
        finalPlan: undefined,
        step: routeChanged ? 0 : state.step,
        reviewKeys: reviewKeys(state, draft.plan),
        error: undefined,
        saveFailed: false,
    };
}

/** Coordinates draft/history, navigation and save status; network effects stay in the owning hook. */
export function autoAssignReducer(
    state: AutoAssignState,
    action: AutoAssignAction
): AutoAssignState {
    if (state.saving && !['save-failed', 'save-finished'].includes(action.type)) return state;

    switch (action.type) {
        case 'edit-route':
            return updateDraft(state, action.draft, true);

        case 'choose': {
            const rows = state.draft.plan.rows.filter((row) =>
                action.keys.includes(assignmentKey(row))
            );
            if (!rows.length) return state;

            const directJourneys = (state.draft.directJourneys || []).filter(
                (key) => !action.keys.includes(key)
            );
            const plan =
                action.choice === 'extend'
                    ? state.draft.plan
                    : confirmJourney(state.draft.plan, rows, action.choice);

            return updateDraft(
                state,
                {
                    ...state.draft,
                    plan,
                    directJourneys:
                        action.choice === 'extend'
                            ? [...directJourneys, ...action.keys]
                            : directJourneys,
                },
                false
            );
        }

        case 'undo':
        case 'redo': {
            const undo = action.type === 'undo';
            const source = undo ? state.past : state.future;
            const draft = source[source.length - 1];
            if (!draft) return state;

            return {
                ...state,
                draft,
                past: undo ? state.past.slice(0, -1) : [...state.past, state.draft],
                future: undo ? [...state.future, state.draft] : state.future.slice(0, -1),
                step: 0,
                finalPlan: undefined,
                error: undefined,
                saveFailed: false,
                reviewKeys: reviewKeys(state, draft.plan),
            };
        }

        case 'back':
            return {
                ...state,
                step: state.step === 2 ? 1 : 0,
                finalPlan: undefined,
                error: undefined,
            };
        case 'edit':
            return {...state, step: 0, finalPlan: undefined};
        case 'review-choices':
            return {...state, step: 1, showSteps: true, finalPlan: undefined, error: undefined};
        case 'review-final':
            return {...state, step: 2, showSteps: true, finalPlan: action.plan, error: undefined};
        case 'view':
            return {...state, selectionView: action.view};
        case 'error':
            return {...state, error: action.message, saveFailed: false};

        case 'save-started':
            return {...state, saving: true, error: undefined, saveFailed: false};
        case 'save-failed':
            return {...state, saving: false, saveFailed: true, error: action.message};
        case 'save-finished':
            return {...state, saving: false};
    }
}
